import React, { useState, useEffect, useCallback, useRef } from 'react';
import { notifyCartUpdated } from '../../utils/cartEvents';
import { useHistory, useLocation } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Box, FileText, Star, ChevronDown, ChevronUp, Info, ExternalLink, Clock, X, AlertTriangle, CheckCircle, AlertCircle, Package, Plus, ChevronRight } from 'lucide-react';
import UserLayout from './UserLayout';
import AdvancedSearch from '../AdvancedSearch';
import NotifyMeButton from '../NotifyMeButton';
import StauffStockModal from '../StauffStockModal';
import BOMComponent from '../shared/BOMComponent';
import { showNotification, showError } from '../../utils/notificationHandler';
import { logCustomerSearch as writeSearchLog, buildShownSnapshot, buildResultsSnapshot } from '../../utils/searchLogger';
import { useInquiryLists } from '../../hooks/useInquiryLists';
import { useOrderingEnabled } from '../../hooks/useOrderingEnabled';

const LoadingOverlay = ({ message, subMessage }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white p-8 rounded-lg shadow-xl flex flex-col items-center max-w-md mx-4"
    >
      <div className="relative">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-4 border-brand border-t-transparent rounded-full"
        />
        <motion.div
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <Search className="w-8 h-8 text-brand" />
        </motion.div>
      </div>
      <h3 className="mt-6 text-xl font-semibold text-gray-900">{message}</h3>
      {subMessage && (
        <p className="mt-2 text-sm text-gray-600 text-center">{subMessage}</p>
      )}
      <div className="mt-4 flex space-x-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            animate={{ scale: [1, 1.5, 1] }}
            transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
            className="w-2 h-2 bg-brand rounded-full"
          />
        ))}
      </div>
    </motion.div>
  </div>
);

const SpecificationItem = ({ label, value }) => (
  <div className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
    <span className="font-medium text-gray-600">{label}:</span>
    <span className="text-gray-800">{value}</span>
  </div>
);

// Enhanced Stock Indicator Components
const StockProgressBar = ({ current, total, percentage }) => {
  const getProgressColor = (percentage) => {
    if (percentage > 70) return 'bg-green-500';
    if (percentage > 30) return 'bg-yellow-500';
    if (percentage > 0) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-gray-700">Stock Level</span>
        <span className="text-sm text-gray-600">{percentage.toFixed(0)}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 1, ease: "easeOut" }}
          className={`h-3 rounded-full ${getProgressColor(percentage)} transition-colors duration-500`}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>{current} available</span>
        <span>{total} total</span>
      </div>
    </div>
  );
};

const StockStatusBadge = ({ status, qtyAvailable, orderStatus }) => {
  const getBadgeConfig = () => {
    if (orderStatus) {
      return {
        icon: Package,
        label: orderStatus.trim(),
        className: 'bg-blue-100 text-blue-800 border-blue-200',
        iconColor: 'text-blue-600'
      };
    }

    if (qtyAvailable === 0) {
      return {
        icon: AlertCircle,
        label: 'Out of Stock',
        className: 'bg-red-100 text-red-800 border-red-200',
        iconColor: 'text-red-600'
      };
    }

    if (qtyAvailable <= 5) {
      return {
        icon: AlertTriangle,
        label: 'Low Stock',
        className: 'bg-orange-100 text-orange-800 border-orange-200',
        iconColor: 'text-orange-600'
      };
    }

    return {
      icon: CheckCircle,
      label: 'In Stock',
      className: 'bg-green-100 text-green-800 border-green-200',
      iconColor: 'text-green-600'
    };
  };

  const { icon: Icon, label, className, iconColor } = getBadgeConfig();

  return (
    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${className}`}>
      <Icon className={`w-4 h-4 mr-2 ${iconColor}`} />
      {label}
    </div>
  );
};

const EnhancedStockDisplay = ({ result, canViewFullStock, requestedQuantity = 1 }) => {
  const totalStock = canViewFullStock ? result.qty_on_hand || 0 : result.qty_available;
  const availableStock = result.qty_available || 0;
  const percentage = totalStock > 0 ? (availableStock / totalStock) * 100 : 0;

  // Calculate quantity-specific availability
  const canFulfillRequested = availableStock >= requestedQuantity;
  const shortfall = requestedQuantity - availableStock;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
            <Package className="w-4 h-4 mr-1" />
            Local MHS Stock (Perth)
          </div>
        </div>
        <StockStatusBadge
          status="stock"
          qtyAvailable={availableStock}
          orderStatus={result.order_status}
        />
      </div>

      {/* Quantity-specific availability message */}
      {requestedQuantity > 1 && (
        <div className={`p-3 rounded-lg border-l-4 ${canFulfillRequested
          ? 'bg-green-50 border-green-400'
          : availableStock > 0
            ? 'bg-orange-50 border-orange-400'
            : 'bg-red-50 border-red-400'
          }`}>
          <div className="flex items-center">
            {canFulfillRequested ? (
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            ) : availableStock > 0 ? (
              <AlertTriangle className="w-5 h-5 text-orange-600 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            )}
            <div>
              <p className={`font-semibold ${canFulfillRequested
                ? 'text-green-800'
                : availableStock > 0
                  ? 'text-orange-800'
                  : 'text-red-800'
                }`}>
                {canFulfillRequested
                  ? `Can fulfill all ${requestedQuantity} pieces locally`
                  : availableStock > 0
                    ? `Only ${availableStock} of ${requestedQuantity} available locally`
                    : `None of ${requestedQuantity} available locally`
                }
              </p>
              {!canFulfillRequested && availableStock > 0 && (
                <p className="text-sm text-orange-700 mt-1">
                  {shortfall} more piece{shortfall !== 1 ? 's' : ''} needed - check Stauff stock over east
                </p>
              )}
              {availableStock === 0 && (
                <p className="text-sm text-red-700 mt-1">
                  Check Stauff stock over east for availability
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {canViewFullStock && totalStock > 0 && (
        <StockProgressBar
          current={availableStock}
          total={totalStock}
          percentage={percentage}
        />
      )}

      <div className="space-y-2">
        {canViewFullStock && (
          <>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 flex items-center">
                <Package className="w-4 h-4 mr-2" />
                On Hand (Perth):
              </span>
              <span className="font-medium">{result.qty_on_hand} {result.unit_code}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Allocated:
              </span>
              <span className="font-medium">{result.qty_allocated} {result.unit_code}</span>
            </div>
          </>
        )}
        <div className="flex justify-between items-center pt-2 border-t border-blue-200">
          <span className="text-gray-800 font-medium flex items-center">
            <CheckCircle className="w-4 h-4 mr-2" />
            Available Locally:
          </span>
          <div className="flex items-center">
            <span className={`font-bold text-lg ${availableStock > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {availableStock} {result.unit_code}
            </span>
            {availableStock > 0 && (
              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                Perth Stock
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Tabbed Interface Components
const TabContainer = ({ children, activeTab, onTabChange, tabs }) => {
  return (
    <div className="bg-gray-50 rounded-lg">
      <div className="border-b border-gray-200">
        <nav className="flex space-x-1 p-1" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${activeTab === tab.id
                ? 'bg-white text-blue-700 shadow-sm border border-gray-200'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  );
};

const OverviewPanel = ({ result }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h4 className="font-medium text-gray-800 mb-3">Key Information</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Part Number:</span>
            <span className="font-medium">{result.prod_code}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Product Group:</span>
            <span className="font-medium">{result.prod_group}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Unit:</span>
            <span className="font-medium">{result.unit_code}</span>
          </div>
        </div>
      </div>
      <div>
        <h4 className="font-medium text-gray-800 mb-3">Quick Specs</h4>
        {result.specifications && Object.keys(result.specifications).length > 0 ? (
          <div className="space-y-2 text-sm">
            {Object.entries(result.specifications)
              .slice(0, 3)
              .map(([key, value]) => (
                <div key={key} className="flex justify-between">
                  <span className="text-gray-600">{key.replace(/_/g, ' ')}:</span>
                  <span className="font-medium">{typeof value === 'number' ? value.toFixed(2) : value}</span>
                </div>
              ))
            }
          </div>
        ) : (
          <p className="text-gray-500 italic text-sm">No specifications available</p>
        )}
      </div>
    </div>
  </div>
);

const SpecificationsPanel = ({ result }) => (
  <div>
    <h4 className="font-medium text-gray-800 mb-4">Technical Specifications</h4>
    {result.specifications && Object.keys(result.specifications).length > 0 ? (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
        {Object.entries(result.specifications)
          .filter(([key]) => key.toLowerCase() !== 'volume_cc')
          .map(([key, value]) => (
            <SpecificationItem
              key={key}
              label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              value={typeof value === 'number' ? value.toFixed(2) : value}
            />
          ))
        }
      </div>
    ) : (
      <p className="text-gray-500 italic">No specifications available</p>
    )}
  </div>
);

const AdditionalInfoPanel = ({ result }) => (
  <div>
    <h4 className="font-medium text-gray-800 mb-4">Additional Information</h4>
    {result.article_description ? (
      <div className="space-y-4">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <h5 className="font-medium text-gray-700 mb-2">Article Description</h5>
          <p className="text-gray-600 text-sm leading-relaxed">{result.article_description}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <h5 className="font-medium text-gray-700 mb-2">Product Classification</h5>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Product Group:</span>
            <span className="font-medium text-gray-800">{result.prod_group}</span>
          </div>
        </div>
      </div>
    ) : (
      <p className="text-gray-500 italic">No additional information available</p>
    )}
  </div>
);

// Search Panel Component
const UserPartEnquiry = () => {
  const { enabled: orderingEnabled, message: orderingMessage } = useOrderingEnabled();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('exact');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [crossReferences, setCrossReferences] = useState(null);
  const [productNotAllowed, setProductNotAllowed] = useState(false);
  // Search history value is displayed by the global header search; here we only
  // populate it (via setSearchHistory) so the write stays in sync.
  const [, setSearchHistory] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [canViewBOMComponents, setCanViewBOMComponents] = useState(true);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [userId, setUserId] = useState(null);
  const [accountId, setAccountId] = useState(localStorage.getItem('accountId'));
  const [canViewFullStock, setCanViewFullStock] = useState(true);
  const [stauffModalOpen, setStauffModalOpen] = useState(false);
  const [stauffModalData, setStauffModalData] = useState(null);
  const [stauffAutoCheck, setStauffAutoCheck] = useState(false);
  const [stauffCountdown, setStauffCountdown] = useState(null);
  const [showStauffCountdown, setShowStauffCountdown] = useState(false);
  const [cartQuantity, setCartQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('overview');
  const [usedCustomMapping, setUsedCustomMapping] = useState(null);
  const [lastProcessedUrlParam, setLastProcessedUrlParam] = useState(null);
  const [initialLoading, setInitialLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Preparing search...');
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [selectedInquiryList, setSelectedInquiryList] = useState(null);
  const [inquiryNotes, setInquiryNotes] = useState('');
  const [newInquiryName, setNewInquiryName] = useState('');
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [accountData, setAccountData] = useState(null);
  const [accountDataLoaded, setAccountDataLoaded] = useState(false);
  const productContainerRef = useRef(null);
  const history = useHistory();
  const location = useLocation();

  // Use inquiry lists hook
  const { inquiryLists, createList } = useInquiryLists(userId);
  const draftInquiries = inquiryLists.filter(list => list.status === 'draft');

  const addToSearchHistory = useCallback(async (term, type) => {
    if (userId) {
      const { error } = await supabase
        .from('search_history')
        .insert({ user_id: userId, part_number: term, search_type: type });

      if (error) {
        console.error('Error adding search to history:', error);
      } else {
        const { data, error: fetchError } = await supabase
          .from('search_history')
          .select('part_number, search_type')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(40);

        if (fetchError) {
          console.error('Error fetching search history:', fetchError);
        } else {
          setSearchHistory(data);
        }
      }
    }
  }, [userId]);

  // Enhanced search logging for customer analytics
  // extra: { shown, results } — snapshot of the price/stock/parts shown to the user
  const logCustomerSearch = useCallback((searchTerm, searchType, searchSuccess, resultsCount = 0, partFound = null, extra = {}) => (
    writeSearchLog({
      userId,
      account: accountData,
      searchTerm,
      searchType,
      success: searchSuccess,
      resultsCount,
      partFound,
      source: 'part_enquiry',
      ...extra,
    })
  ), [userId, accountData]);

  const fetchUserData = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
        const { data: accountData, error: accountError } = await supabase
          .from('accounts')
          .select('id, can_view_bom_components, can_view_full_stock, is_admin, is_staff, company_name, full_name')
          .eq('id', user.id)
          .single();

        if (accountError) throw accountError;
        setAccountId(accountData.id);
        setCanViewBOMComponents(accountData.can_view_bom_components);
        setCanViewFullStock(accountData.can_view_full_stock);
        setAccountData(accountData);

        const { data: historyData, error: historyError } = await supabase
          .from('search_history')
          .select('part_number, search_type')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(10);

        if (historyError) throw historyError;
        setSearchHistory(historyData);

        const { data: favoritesData, error: favoritesError } = await supabase
          .from('favorites')
          .select('part_number')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (favoritesError) throw favoritesError;
        setFavorites(favoritesData.map(item => item.part_number));
        
        // Set account data loaded flag
        setAccountDataLoaded(true);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      showError(error, 'Failed to fetch user data. Please try again later.');
    }
  }, []);

  const checkCustomerPartNumber = async (partNumber) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No authenticated user found for custom part lookup');
        return null;
      }

      console.log(`Checking custom mapping for part: ${partNumber}, user: ${user.id}`);

      const { data, error } = await supabase
        .from('part_number_mappings')
        .select('mhs_part_number, customer_part_number')
        .eq('customer_part_number', partNumber)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.warn('Error checking customer part number:', error);
        return null;
      }

      if (data?.mhs_part_number) {
        console.log(`Custom mapping found: ${partNumber} -> ${data.mhs_part_number}`);
        return {
          mhsPartNumber: data.mhs_part_number,
          originalCustomerPart: partNumber
        };
      }

      console.log(`No custom mapping found for: ${partNumber}`);
      return null;
    } catch (error) {
      console.error('Exception in checkCustomerPartNumber:', error);
      return null;
    }
  };

  const handleSearch = useCallback(async (e, overrideSearchType, directPartNumber) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    setSearchResults([]);
    setCrossReferences(null);
    setProductNotAllowed(false);
    setUsedCustomMapping(null);

    try {
      const searchTermToUse = directPartNumber || searchTerm;
      let mhsPartNumber = searchTermToUse;
      let currentSearchType = overrideSearchType || searchType;

      // Set progressive loading messages
      setLoadingMessage(
        currentSearchType === 'exact' ?
          `Finding exact match for "${searchTermToUse}"...` :
          `Searching ${currentSearchType} matches for "${searchTermToUse}"...`
      );

      if (currentSearchType === 'exact') {
        const customMapping = await checkCustomerPartNumber(searchTermToUse);
        if (customMapping) {
          mhsPartNumber = customMapping.mhsPartNumber;
          setUsedCustomMapping(customMapping);
          showNotification(`Custom part number '${customMapping.originalCustomerPart}' found! Converting to '${customMapping.mhsPartNumber}'`, 'info');
        }
      }

      // Update loading message before API call
      setLoadingMessage('Querying database...');

      if (currentSearchType === 'exact') {
        // Exact match uses the legacy Progress-backed endpoint.
        const response = await fetch('/api/enquire/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ partNumber: mhsPartNumber, account_id: accountId }),
        });

        if (!response.ok) {
          const text = await response.text();
          console.error('Server response:', text);
          throw new Error(`HTTP error! status: ${response.status}, body: ${text}`);
        }

        let data;
        try {
          data = await response.json();
        } catch (jsonError) {
          console.error('JSON parsing error:', jsonError);
          const text = await response.text();
          console.error('Raw response:', text);
          throw new Error(`Failed to parse JSON: ${jsonError.message}. Raw response: ${text}`);
        }

        setLoadingMessage('Loading product details...');

        if (data.cross_references) {
          setCrossReferences(data.cross_references);
          showNotification('Cross-references found for the given part number.', 'success');
          // Log successful search with cross-references
          await logCustomerSearch(searchTermToUse, currentSearchType, true, data.cross_references.length, null,
            { results: buildResultsSnapshot(data.cross_references) });
        } else if (data.bom_code || data.prod_code) {
          // Navigate to dedicated product page for consistent UX
          const partNumber = data.prod_code || data.bom_code;
          addToSearchHistory(searchTermToUse, currentSearchType);
          // Log successful exact match
          await logCustomerSearch(searchTermToUse, currentSearchType, true, 1, partNumber,
            { shown: buildShownSnapshot(data, canViewFullStock) });
          showNotification('Part information retrieved successfully.', 'success');

          // Navigate to dedicated product page (already logged, so the product page skips its own log)
          history.push(`/user/product/${partNumber}`, { searchLogged: true });
        } else {
          // No exact hit: fall through to smart search instead of dead-ending, so
          // every search box behaves the same. The smart search below logs the
          // final outcome, so the miss isn't logged twice.
          currentSearchType = 'fuzzy';
          setSearchType('fuzzy');
          setLoadingMessage(`No exact match for "${searchTermToUse}" - searching similar parts...`);
        }
      }

      if (currentSearchType !== 'exact') {
        setLoadingMessage('Formatting search results...');
        // Clear URL parameter for non-exact searches
        history.push(location.pathname);
        if (lastProcessedUrlParam) setLastProcessedUrlParam(null);

        // Use Elasticsearch for fuzzy and description searches (same as Advanced Search)
        try {
          const baseUrl = `https://api.mhsonline.au/api/search/?search_term=${encodeURIComponent(searchTermToUse)}&limit=50`;

          if (currentSearchType === 'fuzzy') {
            // For fuzzy search, combine both part number and description results
            const [fuzzyResponse, descResponse] = await Promise.all([
              fetch(`${baseUrl}&search_type=fuzzy`),
              fetch(`${baseUrl}&search_type=description`)
            ]);

            if (fuzzyResponse.ok && descResponse.ok) {
              const fuzzyData = await fuzzyResponse.json();
              const descData = await descResponse.json();

              // Combine and deduplicate results
              const combined = [...fuzzyData, ...descData];
              const uniqueResults = combined.filter((item, index, self) =>
                index === self.findIndex(t => t.prod_code === item.prod_code)
              );

              setSearchResults(uniqueResults);

              if (uniqueResults.length === 0) {
                showNotification('No results found for your search.', 'info');
                await logCustomerSearch(searchTermToUse, currentSearchType, false, 0, null);
              } else {
                showNotification(`Found ${uniqueResults.length} results`, 'success');
                addToSearchHistory(searchTermToUse, currentSearchType);
                await logCustomerSearch(searchTermToUse, currentSearchType, true, uniqueResults.length, null,
                  { results: buildResultsSnapshot(uniqueResults) });
              }
            } else {
              throw new Error('Elasticsearch search failed');
            }
          } else if (currentSearchType === 'description') {
            // For description search only
            const response = await fetch(`${baseUrl}&search_type=description`);

            if (response.ok) {
              const resultArray = await response.json();
              setSearchResults(resultArray);

              if (resultArray.length === 0) {
                showNotification('No results found for your search.', 'info');
                await logCustomerSearch(searchTermToUse, currentSearchType, false, 0, null);
              } else {
                showNotification(`Found ${resultArray.length} results`, 'success');
                addToSearchHistory(searchTermToUse, currentSearchType);
                await logCustomerSearch(searchTermToUse, currentSearchType, true, resultArray.length, null,
                  { results: buildResultsSnapshot(resultArray) });
              }
            } else {
              throw new Error('Elasticsearch search failed');
            }
          }
        } catch (elasticError) {
          console.warn('Elasticsearch search failed, falling back to legacy search:', elasticError);

          // Fallback to legacy /api/enquire/ endpoint — fetched lazily only when
          // Elasticsearch fails, so a normal Smart Search makes no extra call.
          let resultArray = [];
          try {
            const legacyResponse = await fetch('/api/enquire/', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ searchTerm: searchTermToUse, searchType: currentSearchType, account_id: accountId }),
            });
            if (legacyResponse.ok) {
              const legacyData = await legacyResponse.json();
              resultArray = Array.isArray(legacyData) ? legacyData : [];
            }
          } catch (legacyError) {
            console.error('Legacy fallback search failed:', legacyError);
          }

          setSearchResults(resultArray);

          if (resultArray.length === 0) {
            showNotification('No results found for your search.', 'info');
            await logCustomerSearch(searchTermToUse, currentSearchType, false, 0, null);
          } else {
            showNotification(`Found ${resultArray.length} results`, 'success');
            addToSearchHistory(searchTermToUse, currentSearchType);
            await logCustomerSearch(searchTermToUse, currentSearchType, true, resultArray.length, null,
              { results: buildResultsSnapshot(resultArray) });
          }
        }
      }
    } catch (error) {
      if (error.message.includes('Product Not Allowed')) {
        setProductNotAllowed(true);
        showNotification('This product is not available for enquiry or purchase.', 'error');
      } else if (error.message.includes('Database error')) {
        showError(error, 'A database connection error occurred. Please try again later or contact support.');
        console.error('Database error:', error.message);
      } else {
        showError(error, 'An error occurred while searching. Please try again.');
        console.error('Error:', error);
      }
    } finally {
      setLoading(false);
      setInitialLoading(false);
      setLoadingMessage('Preparing search...');
    }
  }, [searchTerm, searchType, accountId, addToSearchHistory, history, location.pathname, lastProcessedUrlParam, logCustomerSearch, canViewFullStock]);

  const handleResultClick = useCallback((partNumber) => {
    // Store search results in sessionStorage so we can return to them
    if (searchResults.length > 0) {
      sessionStorage.setItem('searchResults', JSON.stringify(searchResults));
      sessionStorage.setItem('searchTerm', searchTerm);
      sessionStorage.setItem('searchType', searchType);
      sessionStorage.setItem('hasSearchResults', 'true');
    }
    
    // Navigate to dedicated product page
    history.push(`/user/product/${partNumber}`, { fromSearch: true });
  }, [searchResults, searchTerm, searchType, history]);

  const handleAddToFavorites = async (term) => {
    if (userId && !favorites.includes(term)) {
      const { error } = await supabase
        .from('favorites')
        .insert({ user_id: userId, part_number: term });

      if (error) {
        console.error('Error adding to favorites:', error);
        showError(error, 'Failed to add to favourites');
      } else {
        setFavorites(prevFavorites => [term, ...prevFavorites]);
        showNotification(`Added "${term}" to favourites`, 'success');
      }
    }
  };

  const addToCart = (item, quantity = 1) => {
    if (!orderingEnabled) {
      showNotification(orderingMessage, 'error');
      return;
    }
    const cartItem = {
      partNumber: item.prod_code || item.bom_code,
      description: item.Description || item.bom_description,
      quantity: quantity,
      price: item.buy_price,
      isBOM: !!item.bom_code
    };

    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');

    const existingItemIndex = existingCart.findIndex(
      cartItem => cartItem.partNumber === item.prod_code || cartItem.partNumber === item.bom_code
    );

    if (existingItemIndex >= 0) {
      existingCart[existingItemIndex].quantity += quantity;
    } else {
      existingCart.push(cartItem);
    }

    localStorage.setItem('cart', JSON.stringify(existingCart));
    notifyCartUpdated();
    showNotification(`${quantity} item(s) added to cart successfully`, 'success');
  };

  const shouldAutoCheckStauff = (result) => {
    return result.prod_group?.startsWith('H') && result.qty_available === 0;
  };

  const openStauffModal = (partNumber, autoCheck = false, quantity = 1) => {
    setStauffModalData({ partNumber, initialQuantity: quantity });
    setStauffAutoCheck(autoCheck);
    setStauffModalOpen(true);
  };

  const closeStauffModal = () => {
    setStauffModalOpen(false);
    setStauffModalData(null);
    setStauffAutoCheck(false);
  };

  const handleStauffAddToCart = (cartItem) => {
    if (!orderingEnabled) {
      showNotification(orderingMessage, 'error');
      return;
    }
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');

    const existingItemIndex = existingCart.findIndex(
      item => item.partNumber === cartItem.partNumber
    );

    if (existingItemIndex >= 0) {
      existingCart[existingItemIndex].quantity += cartItem.quantity;
    } else {
      existingCart.push(cartItem);
    }

    localStorage.setItem('cart', JSON.stringify(existingCart));
    notifyCartUpdated();
  };

  const handleStauffCheckNow = () => {
    setShowStauffCountdown(false);
    setStauffCountdown(null);
    openStauffModal(result.prod_code, true, cartQuantity);
  };

  const handleStauffCancel = () => {
    setShowStauffCountdown(false);
    setStauffCountdown(null);
  };

  // Handle add to inquiry list
  const handleAddToInquiry = async () => {
    if (showCreateNew) {
      // Create new inquiry list
      if (!newInquiryName.trim()) {
        showError('Please enter a name for the new inquiry list');
        return;
      }

      try {
        const newListId = await createList(newInquiryName, null);
        setSelectedInquiryList(newListId);
        setShowCreateNew(false);
        setNewInquiryName('');

        // Proceed to add item to newly created list
        await addItemToInquiry(newListId);
      } catch (error) {
        showError('Failed to create inquiry list: ' + error.message);
      }
    } else if (selectedInquiryList) {
      await addItemToInquiry(selectedInquiryList);
    } else {
      showError('Please select an inquiry list or create a new one');
    }
  };

  const addItemToInquiry = async (inquiryListId) => {
    try {
      const { error } = await supabase.rpc('add_inquiry_item', {
        inquiry_list_id: inquiryListId,
        part_number: result.prod_code,
        description: result.Description,
        quantity: cartQuantity,
        notes: inquiryNotes || null
      });

      if (error) throw error;

      showNotification(`Added ${result.prod_code} to inquiry list!`);
      setShowInquiryModal(false);
      setSelectedInquiryList(null);
      setInquiryNotes('');
      setShowCreateNew(false);
    } catch (error) {
      showError('Failed to add to inquiry: ' + error.message);
    }
  };

  // Add useEffect for checking sessionStorage loading state
  useEffect(() => {
    const searchLoadingData = sessionStorage.getItem('searchLoading');
    if (searchLoadingData) {
      const data = JSON.parse(searchLoadingData);
      // Only consider recent loading states (within 5 seconds)
      if (Date.now() - data.timestamp < 5000) {
        setInitialLoading(true);
        setLoadingMessage(`Searching for "${data.searchTerm}"...`);

        // Clear the flag after component starts its own loading
        setTimeout(() => {
          sessionStorage.removeItem('searchLoading');
        }, 100);
      }
    }
  }, []);

  useEffect(() => {
    fetchUserData();
    
    // Restore search results if returning from product page
    const savedResults = sessionStorage.getItem('searchResults');
    const savedSearchTerm = sessionStorage.getItem('searchTerm');
    const savedSearchType = sessionStorage.getItem('searchType');
    
    if (savedResults && savedSearchTerm) {
      try {
        const parsedResults = JSON.parse(savedResults);
        setSearchResults(parsedResults);
        setSearchTerm(savedSearchTerm);
        setSearchType(savedSearchType || 'exact');
        
        // Don't clear immediately - wait for user to navigate away
        // This keeps results when switching between product pages
      } catch (error) {
        console.error('Error restoring search results:', error);
        sessionStorage.removeItem('searchResults');
        sessionStorage.removeItem('searchTerm');
        sessionStorage.removeItem('searchType');
        sessionStorage.removeItem('hasSearchResults');
      }
    }
  }, [fetchUserData]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const partNumberParam = params.get('partNumber');
    const searchParam = params.get('search');

    // The header and dashboard searches drive this page via URL params:
    //   ?partNumber=<term>                exact match, falling back to smart search
    //   ?search=<term>&type=<searchType>  smart search only (search history links)
    const activeTerm = partNumberParam || searchParam;
    const activeType = partNumberParam ? 'exact' : (params.get('type') || 'fuzzy');
    // Unique key (type + term) so we only run each URL-driven search once,
    // even when we later update the URL programmatically.
    const paramKey = activeTerm ? `${activeType}:${activeTerm}` : null;

    if (paramKey && paramKey !== lastProcessedUrlParam && accountDataLoaded) {
      setLastProcessedUrlParam(paramKey);
      setSearchTerm(activeTerm);
      setSearchType(activeType);
      setLoadingMessage(`Searching for "${activeTerm}"...`);
      handleSearch({ preventDefault: () => { } }, activeType, activeTerm);
    } else if (!paramKey && lastProcessedUrlParam) {
      // URL parameters were removed, clear the tracking
      setLastProcessedUrlParam(null);
      setInitialLoading(false); // Clear any residual loading state
    }
  }, [location.search, lastProcessedUrlParam, handleSearch, accountDataLoaded]);

  useEffect(() => {
    if (result && shouldAutoCheckStauff(result)) {
      setShowStauffCountdown(true);
      setStauffCountdown(4);

      const countdownInterval = setInterval(() => {
        setStauffCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setShowStauffCountdown(false);
            openStauffModal(result.prod_code, true, cartQuantity);
            return null;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [result, cartQuantity]);

  // Auto-scroll to product when it loads (Phase 1 quick fix)
  useEffect(() => {
    if (result && productContainerRef.current) {
      // Small delay to ensure render is complete
      setTimeout(() => {
        productContainerRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [result]);

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Part Enquiry</h1>
            <nav className="text-sm text-gray-600 mt-1">
              <button
                onClick={() => history.push('/user/dashboard')}
                className="hover:text-brand transition-colors duration-200"
              >
                Dashboard
              </button>
              <span className="mx-2">{'>'}</span>
              <span>Part Enquiry</span>
            </nav>
          </div>
        </div>

        {/* Advanced Search Toggle */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <button
            onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
            className="w-full flex items-center justify-between p-4 text-left font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200"
          >
            <span>Advanced Search with Stauff Suggestions</span>
            {showAdvancedSearch ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>

          <AnimatePresence>
            {showAdvancedSearch && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="border-t border-gray-200"
              >
                <div className="p-4">
                  <AdvancedSearch
                    onResultSelect={handleResultClick}
                    onAddToCart={handleStauffAddToCart}
                    userId={userId}
                    accountData={accountData}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Product Not Allowed Warning */}
        {productNotAllowed && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <div>
                <h3 className="font-semibold text-red-800">Product Not Allowed</h3>
                <p className="text-red-700 text-sm">This product is not available for enquiry or purchase.</p>
              </div>
            </div>
          </div>
        )}

        {/* Search Results - Enhanced Card Layout */}
        {searchResults.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Search Results ({searchResults.length} found)
            </h2>
            <div className="grid grid-cols-1 gap-3">
              {searchResults.map((result, index) => (
                <button
                  key={index}
                  onClick={() => handleResultClick(result.prod_code)}
                  className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-brand hover:bg-gray-50 transition-all duration-200 text-left group"
                >
                  <img
                    src={result.small_image_url || 'https://syd1.digitaloceanspaces.com/cdn-assets.mhs/images/placeholders/mhs_placeholder.png'}
                    alt={result.prod_code}
                    className="h-16 w-16 rounded object-cover flex-shrink-0"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://syd1.digitaloceanspaces.com/cdn-assets.mhs/images/placeholders/mhs_placeholder.png';
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 text-lg group-hover:text-brand transition-colors duration-200">
                      {result.prod_code}
                    </div>
                    <div className="text-sm text-gray-600 mt-1 line-clamp-2">
                      {result.Description}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-brand transition-colors duration-200 flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Cross References */}
        {crossReferences && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Cross References</h2>
            <p className="mb-4 text-gray-600">
              The part number you entered is an alternative part. Here are the corresponding main parts:
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alternate Part</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alternate Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Part</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Description</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {crossReferences.map((ref, index) => (
                    <tr key={index} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleResultClick(ref.prod_code)}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{ref.alt_prod_code}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{ref.alt_description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{ref.prod_code}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{ref.main_description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Product Result */}
        {result && (
          <div ref={productContainerRef} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {result.bom_code ? (
              <div className="p-6">
                <BOMComponent
                  bom={result}
                  addToCart={() => addToCart(result)}
                  canViewComponents={canViewBOMComponents}
                />
              </div>
            ) : (
              <div>
                {/* Header Section */}
                <div className="px-6 py-6 bg-gray-50 border-b border-gray-200">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">{result.prod_code}</h2>
                      <p className="text-gray-600 text-lg">{result.Description}</p>
                      {usedCustomMapping && (
                        <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Using custom mapping: {usedCustomMapping.originalCustomerPart} → {usedCustomMapping.mhsPartNumber}
                        </div>
                      )}
                    </div>
                    <div className="mt-4 lg:mt-0">
                      <button
                        onClick={() => handleAddToFavorites(result.prod_code || result.bom_code)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand transition-colors duration-200"
                      >
                        <Star className="mr-2" size={16} />
                        Add to Favorites
                      </button>
                    </div>
                  </div>
                </div>

                {/* Main Content Area */}
                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Left Column - Product Image */}
                    <div className="lg:col-span-5">
                      <div className="bg-gray-50 rounded-lg p-6 h-full flex flex-col items-center justify-center min-h-[400px]">
                        <div className="w-full h-80 flex items-center justify-center">
                          <img
                            src={result.large_image_url}
                            alt="Product"
                            className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://syd1.digitaloceanspaces.com/cdn-assets.mhs/images/placeholders/mhs_placeholder.png';
                            }}
                          />
                        </div>
                        <p className="text-sm text-gray-500 mt-4 text-center italic">
                          Product illustration - actual item may vary
                        </p>
                      </div>
                    </div>

                    {/* Right Column - Stock & Pricing + Actions */}
                    <div className="lg:col-span-7 space-y-6">

                      {/* Stock & Pricing Panel */}
                      <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                        <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                          <Box className="mr-2" size={20} />
                          Stock & Pricing Information
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <EnhancedStockDisplay
                              result={result}
                              canViewFullStock={canViewFullStock}
                            />
                          </div>

                          <div>
                            <h4 className="font-medium text-blue-800 mb-3">Pricing</h4>
                            <div className="space-y-2">
                              {result.buy_price && (
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600">Your Price:</span>
                                  <span className="text-2xl font-bold text-green-600">
                                    ${parseFloat(result.buy_price).toFixed(2)}
                                  </span>
                                </div>
                              )}
                              {result.base_price && (
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600">List Price:</span>
                                  <span className="font-medium text-gray-800">
                                    ${parseFloat(result.base_price).toFixed(2)}
                                  </span>
                                </div>
                              )}
                              <p className="text-xs text-gray-500 mt-2">
                                Price per {result.unit_code}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Actions Panel */}
                      <div className="bg-brand rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-brand-dark mb-4">Actions</h3>

                        <div className="flex flex-col sm:flex-row gap-4 items-center mb-4">
                          <div className="flex items-center gap-3">
                            <label className="text-sm font-medium text-brand-dark">Quantity:</label>
                            <input
                              type="number"
                              min="1"
                              value={cartQuantity}
                              onChange={(e) => setCartQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                              className="w-20 px-3 py-2 text-center border border-brand-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-dark focus:border-transparent"
                            />
                          </div>
                          {orderingEnabled ? (
                            <button
                              onClick={() => addToCart(result, cartQuantity)}
                              className="flex-1 sm:flex-none bg-brand-dark text-brand py-3 px-6 rounded-lg font-semibold hover:bg-gray-800 transition-colors duration-200 flex items-center justify-center min-w-[150px]"
                            >
                              Add to Cart
                            </button>
                          ) : (
                            <span className="text-sm font-medium text-brand-dark">
                              Online ordering is currently unavailable
                            </span>
                          )}
                          <button
                            onClick={() => setShowInquiryModal(true)}
                            className="flex-1 sm:flex-none bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 transition-colors duration-200 flex items-center justify-center min-w-[150px]"
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Add to Inquiry
                          </button>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <button
                            onClick={() => openStauffModal(result.prod_code, false, cartQuantity)}
                            className="bg-blue-600 text-white py-2 px-4 rounded-lg text-sm hover:bg-blue-700 flex items-center justify-center transition-colors duration-200"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Check Stauff Stock
                          </button>

                          {result.qty_available === 0 && !result.order_status && (
                            <NotifyMeButton
                              partNumber={result.prod_code || result.bom_code}
                              isInStock={false}
                            />
                          )}
                        </div>

                        {/* Stauff Countdown Display */}
                        {showStauffCountdown && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <motion.div
                                  animate={{ scale: [1, 1.2, 1] }}
                                  transition={{ duration: 1, repeat: Infinity }}
                                  className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full mr-3"
                                >
                                  <Clock className="w-4 h-4" />
                                </motion.div>
                                <div>
                                  <p className="font-semibold text-blue-800">
                                    Auto-checking Stauff stock in {stauffCountdown} second{stauffCountdown !== 1 ? 's' : ''}...
                                  </p>
                                  <p className="text-blue-600 text-xs">
                                    Checking supplier availability for hydraulic parts
                                  </p>
                                </div>
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={handleStauffCheckNow}
                                  className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors duration-200"
                                >
                                  Check Now
                                </button>
                                <button
                                  onClick={handleStauffCancel}
                                  className="px-2 py-1 text-blue-600 hover:text-blue-800 transition-colors duration-200"
                                  aria-label="Cancel Stauff stock check"
                                >
                                  <X className="w-4 h-4" aria-hidden="true" />
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        )}

                        <p className="text-xs text-gray-600 mt-4 italic">
                          Stock information is not guaranteed to be 100% accurate. If in doubt, please contact us.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tabbed Interface Section */}
                <div className="px-6 pb-6">
                  <TabContainer
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    tabs={[
                      { id: 'overview', label: 'Overview', icon: Info },
                      { id: 'specifications', label: 'Specifications', icon: FileText },
                      { id: 'additional', label: 'Additional Info', icon: Package }
                    ]}
                  >
                    {activeTab === 'overview' && <OverviewPanel result={result} />}
                    {activeTab === 'specifications' && <SpecificationsPanel result={result} />}
                    {activeTab === 'additional' && <AdditionalInfoPanel result={result} />}
                  </TabContainer>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Disclaimer */}
        <div className="bg-gray-100 rounded-lg p-6 text-gray-700 text-sm">
          <p className="font-semibold mb-2">Disclaimer:</p>
          <p>
            While we strive to provide accurate and up-to-date information, please note that the content on this page may contain omissions or errors.
            The information presented here is for general guidance only and should not be relied upon as complete or exact.
            For the most current and accurate details, please contact our customer support team.
          </p>
        </div>

        {/* Loading Overlay */}
        <AnimatePresence>
          {(loading || initialLoading) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <LoadingOverlay
                message={loadingMessage}
                subMessage="Please wait while we search our database..."
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stauff Stock Modal */}
        <StauffStockModal
          isOpen={stauffModalOpen}
          onClose={closeStauffModal}
          partNumber={stauffModalData?.partNumber}
          autoCheck={stauffAutoCheck}
          onAddToCart={handleStauffAddToCart}
          initialQuantity={stauffModalData?.initialQuantity || 1}
        />

        {/* Add to Inquiry Modal */}
        <AnimatePresence>
          {showInquiryModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
              onClick={() => {
                setShowInquiryModal(false);
                setShowCreateNew(false);
                setSelectedInquiryList(null);
                setInquiryNotes('');
                setNewInquiryName('');
              }}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
              >
                <h2 className="text-xl font-bold mb-4">Add to Inquiry List</h2>

                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600">Adding:</div>
                  <div className="font-medium text-gray-900">{result?.prod_code}</div>
                  <div className="text-sm text-gray-600">{result?.Description}</div>
                  <div className="text-sm text-gray-600 mt-1">Quantity: {cartQuantity}</div>
                </div>

                {!showCreateNew ? (
                  <>
                    <div className="space-y-2 mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Inquiry List:
                      </label>
                      {draftInquiries.length > 0 ? (
                        draftInquiries.map(list => (
                          <div
                            key={list.id}
                            onClick={() => setSelectedInquiryList(list.id)}
                            className={`p-3 border rounded-lg cursor-pointer transition-colors ${selectedInquiryList === list.id
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-300 hover:border-purple-300 hover:bg-gray-50'
                              }`}
                          >
                            <div className="flex items-center">
                              <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${selectedInquiryList === list.id
                                ? 'border-purple-500 bg-purple-500'
                                : 'border-gray-300'
                                }`}>
                                {selectedInquiryList === list.id && (
                                  <CheckCircle className="w-3 h-3 text-white" />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{list.list_name}</div>
                                <div className="text-xs text-gray-500">{list.item_count} items</div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 italic py-2">No draft inquiry lists available</p>
                      )}
                    </div>

                    <button
                      onClick={() => setShowCreateNew(true)}
                      className="w-full mb-4 px-4 py-2 border-2 border-dashed border-purple-300 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <Plus size={20} />
                      Create New Inquiry List
                    </button>
                  </>
                ) : (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Inquiry List Name *
                    </label>
                    <input
                      type="text"
                      value={newInquiryName}
                      onChange={(e) => setNewInquiryName(e.target.value)}
                      placeholder="e.g., Hydraulic Parts Q4 2025"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      autoFocus
                    />
                    <button
                      onClick={() => {
                        setShowCreateNew(false);
                        setNewInquiryName('');
                      }}
                      className="mt-2 text-sm text-gray-600 hover:text-gray-900"
                    >
                      ← Back to selection
                    </button>
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={inquiryNotes}
                    onChange={(e) => setInquiryNotes(e.target.value)}
                    placeholder="Add any special requirements or notes..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowInquiryModal(false);
                      setShowCreateNew(false);
                      setSelectedInquiryList(null);
                      setInquiryNotes('');
                      setNewInquiryName('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddToInquiry}
                    disabled={!showCreateNew && !selectedInquiryList}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {showCreateNew ? 'Create & Add' : 'Add to Inquiry'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </UserLayout>
  );
};

export default UserPartEnquiry;
